import _ from "lodash";
import {
  DATA_GRID_CHECKBOX_STATES,
  TRANSCRIPTS_PERMISSION_TYPES,
} from "./constants";
import {
  STUDENT_ORDER_BY_ENUM,
  STUDENT_SORT_BY_ENUM,
} from "TranscriptsV2/components/SortDropdown";
import { hasPermission, hasPermissionInAnySchool } from "./PermissionHelpers";

export const getUpdatedIsDataAndIsLoading = ({
  node,
  networkStatus,
  isData,
  isLoading,
}) => {
  let isDataUpdated = !_.isEmpty(node);
  let isLoadingUpdated = _.includes([1, 2], networkStatus);

  if (isData !== undefined) {
    isDataUpdated = isData && !_.isEmpty(node);
  }

  if (isLoading !== undefined) {
    isLoadingUpdated = isLoading || _.includes([1, 2], networkStatus);
  }

  return { isData: isDataUpdated, isLoading: isLoadingUpdated };
};

export const getDataGridCheckboxValue = ({ value }) => {
  return value
    ? DATA_GRID_CHECKBOX_STATES.CHECKED
    : DATA_GRID_CHECKBOX_STATES.UNCHECKED;
};

export const getHeadersWithScaledWidths = ({
  headers,
  containerWidth,
  parentDefaultWidth,
}) => {
  const getScaleFactor = (parentWidth, totalPreferred) => {
    return parentWidth / totalPreferred;
  };

  const calculateScaledWidth = ({ scaleFactor, width, minWidth, maxWidth }) => {
    const scaledWidth = Math.floor(width * scaleFactor) - 2; // subtracting 2 for border width

    return _.chain(scaledWidth)
      .thru(w => (_.isNil(minWidth) || w >= minWidth ? w : minWidth))
      .thru(w => (_.isNil(maxWidth) || w <= maxWidth ? w : maxWidth))
      .value();
  };

  const parentWidth = (containerWidth || parentDefaultWidth) - 2; // 2 for border

  const preferredWidths = _.map(headers, "width");
  const minWidths = _.map(headers, "minWidth");
  const maxWidths = _.map(headers, "maxWidth");
  const totalPreferred = _.sum(preferredWidths);
  const scaleFactor = getScaleFactor(parentWidth, totalPreferred);

  const scaledWidths = _.map(preferredWidths, (width, index) =>
    calculateScaledWidth({
      scaleFactor,
      width,
      minWidth: minWidths[index],
      maxWidth: maxWidths[index],
    })
  );

  // Calculate remaining space to redistribute
  const currentTotal = _.sum(scaledWidths);
  const remainingSpace = parentWidth - currentTotal;

  // Find expandable columns and redistribute space
  const redistributeSpace = widths => {
    if (remainingSpace <= 0) return widths;

    const expandableIndices = _.chain(widths)
      .map((width, index) => {
        const maxWidth = maxWidths[index];
        return _.isNil(maxWidth) || width < maxWidth ? index : null;
      })
      .filter(index => !_.isNil(index)) // filter out nulls but not 0, compact removes 0
      .value();

    if (_.isEmpty(expandableIndices)) return widths;

    const spacePerColumn = remainingSpace / expandableIndices.length;

    return _.map(widths, (width, index) => {
      if (!_.includes(expandableIndices, index)) return width;

      const maxWidth = maxWidths[index];
      const newWidth = width + spacePerColumn;

      return _.isNil(maxWidth) || newWidth <= maxWidth ? newWidth : maxWidth;
    });
  };

  const finalWidths = redistributeSpace(scaledWidths);

  return _.map(headers, (item, index) => ({
    ...item,
    width: finalWidths[index] || item.width,
  }));
};

export const getStudentsSortedAndOrderedList = ({
  students,
  sortByValue,
  orderByValue,
}) => {
  return _.orderBy(
    students,
    [
      student => {
        const { name, firstName, lastName } = student;
        switch (sortByValue) {
          case STUDENT_SORT_BY_ENUM.FIRST_NAME:
            return firstName;
          case STUDENT_SORT_BY_ENUM.LAST_NAME:
            return lastName;
          case STUDENT_SORT_BY_ENUM.FULL_NAME:
          default:
            return name;
        }
      },
    ],
    [orderByValue === STUDENT_ORDER_BY_ENUM.A_Z ? "asc" : "desc"]
  );
};

export const getOrderedAcademicTermsInTranscriptAcademicRecordsByAcademicYear = ({
  transcriptAcademicRecordsByAcademicYear,
}) => {
  return _.map(transcriptAcademicRecordsByAcademicYear, academicYearRecord => {
    const orderedAcademicTermWrapper = _.orderBy(
      _.get(academicYearRecord, "academicTermWrapper", []),
      [
        term => _.get(term, "academicTerm.endDate"),
        term => _.get(term, "academicTerm.startDate"),
      ],
      ["asc", "desc"]
    );

    return {
      ...academicYearRecord,
      academicTermWrapper: orderedAcademicTermWrapper,
    };
  });
};

export const getVariablesForTranscriptPermissionSettings = ({
  curriculumPrograms,
}) => {
  const contexts = _.concat(
    [[]],
    _.map(curriculumPrograms, ({ id }) => [
      {
        entityId: id,
        entityType: "CURRICULUM_PROGRAM",
      },
    ])
  );

  return {
    filters: {
      module: "TRANSCRIPTS_V2",
      appType: ["ADMIN_WEB"],
      contexts,
    },
  };
};

const hasChecklistItemPermission = ({ permissionMap, item, schoolId }) => {
  const permissionConfig = _.get(item, "permission");

  if (!permissionConfig) {
    return true;
  }

  const { type, value: permission } = permissionConfig;

  if (type === TRANSCRIPTS_PERMISSION_TYPES.ACCOUNT) {
    return hasPermission({ permissionMap, permission });
  }

  if (type === TRANSCRIPTS_PERMISSION_TYPES.SCHOOL) {
    if (schoolId) {
      return hasPermission({
        permissionMap,
        permission,
        curriculumProgramId: schoolId,
      });
    } else {
      return hasPermissionInAnySchool({ permissionMap, permission });
    }
  }

  return false;
};

export const getFilteredChecklistData = ({
  checklistData,
  permissionMap,
  schoolId,
}) => {
  if (!permissionMap) {
    return checklistData;
  }

  return _.reduce(
    checklistData,
    (result, section) => {
      // Filter children based on permissions
      const filteredChildren = _.filter(section.children, child => {
        if (schoolId && child.id === "CONFIGURE_TRANSCRIPT_TEMPLATE") {
          return false;
        }
        return hasChecklistItemPermission({
          permissionMap,
          item: child,
          schoolId,
        });
      });

      // Only include section if it has visible children
      if (!_.isEmpty(filteredChildren)) {
        result.push({
          ...section,
          children: filteredChildren,
        });
      }

      return result;
    },
    []
  );
};
